// The creator brief in sections, as a brand writes and reads it: the idea,
// what must be in the video, what must not, format labels and example links.
// The model and its cleaning are shared with the admin editor
// (admin/lib/brief.ts), so a brief means the same thing on both sides.

import {useEffect, useRef, useState} from 'react';
import type {Dispatch, KeyboardEvent, SetStateAction} from 'react';
import type {BrandCampaignFile} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {BRIEF_LIMITS, FORMAT_PRESETS, badExampleUrl, cleanBrief, exampleHost} from '../admin/lib/brief';
import type {BriefSections} from '../admin/lib/brief';
import {briefFileUrl, deleteCampaignFile, describe, loadCampaignFiles, uploadCampaignFile} from './api';
import './applicants.css';

const MAX_FILE_BYTES = 100 * 1024 * 1024;

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

export {badExampleUrl, briefIsEmpty, briefText, cleanBrief, emptyBrief, readBrief} from '../admin/lib/brief';
export type {BriefSections} from '../admin/lib/brief';

function Lines({label, hint, items, max, ordered, placeholder, onChange}: {
  label: string;
  hint: string;
  items: string[];
  max: number;
  ordered?: boolean;
  placeholder: string;
  onChange(items: string[]): void;
}) {
  const rows = items.length ? items : [''];
  function set(index: number, value: string) {
    const next = [...rows];
    next[index] = value;
    onChange(next);
  }
  function onKey(event: KeyboardEvent<HTMLInputElement>, index: number) {
    const value = rows[index] ?? '';
    if (event.key === 'Enter' && value.trim() && rows.length < max) {
      event.preventDefault();
      onChange([...rows.slice(0, index + 1), '', ...rows.slice(index + 1)]);
      // The new line exists after the next render; put the cursor in it.
      const box = event.currentTarget.closest('.brief-lines');
      window.setTimeout(() => box?.querySelectorAll('input')[index + 1]?.focus());
    } else if (event.key === 'Backspace' && value === '' && rows.length > 1) {
      event.preventDefault();
      onChange(rows.filter((_, position) => position !== index));
    }
  }
  return (
    <fieldset className="brand-field brief-block">
      <legend><span>{label}</span><small>{hint}</small></legend>
      <div className="brief-lines">
        {rows.map((item, index) => (
          <div className={`brief-line${ordered ? '' : ' avoid'}`} key={index}>
            <i aria-hidden="true">{ordered ? index + 1 : <Icon name="x" size={12} />}</i>
            <input value={item} maxLength={BRIEF_LIMITS.line} placeholder={index === 0 ? placeholder : ''} aria-label={`${label}, line ${index + 1}`}
              onChange={(event) => set(index, event.target.value)} onKeyDown={(event) => onKey(event, index)} />
            {rows.length > 1 || item ? <button type="button" aria-label={`Remove line ${index + 1}`} onClick={() => onChange(rows.filter((_, position) => position !== index))}><Icon name="x" size={14} /></button> : null}
          </div>
        ))}
      </div>
      <button className="text-button brief-add" type="button" disabled={rows.length >= max} onClick={() => onChange([...rows, ''])}><Icon name="plus" size={14} />Add a line</button>
    </fieldset>
  );
}

function CampaignFilesField({brief, campaignId, pendingFiles, onChange, onPendingFilesChange}: {
  brief: BriefSections;
  campaignId?: string;
  pendingFiles: File[];
  onChange: Dispatch<SetStateAction<BriefSections>>;
  onPendingFilesChange(files: File[]): void;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const savedAttached = useRef(new Set(brief.files.map((file) => file.file_id)));
  const [files, setFiles] = useState<BrandCampaignFile[] | null>(campaignId ? null : []);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!campaignId) { setFiles([]); return; }
    let live = true;
    setFiles(null);
    loadCampaignFiles(campaignId).then((result) => { if (live) setFiles(result.files); }).catch((caught) => {
      if (!live) return;
      setFiles([]); setFailed(true); setNotice(describe(caught));
    });
    return () => { live = false; };
  }, [campaignId]);

  function attach(file: Pick<BrandCampaignFile, 'file_id' | 'name'>) {
    if (brief.files.some((item) => item.file_id === file.file_id)) return;
    if (brief.files.length >= BRIEF_LIMITS.files) {
      setFailed(true); setNotice(`A brief can include up to ${BRIEF_LIMITS.files} files.`); return;
    }
    onChange((current) => ({...current, files: [...current.files, {file_id: file.file_id, name: file.name}]}));
  }

  function detach(fileId: string) {
    onChange((current) => ({...current, files: current.files.filter((file) => file.file_id !== fileId)}));
  }

  async function select(selected: File[]) {
    setNotice(''); setFailed(false);
    let selectionError = '';
    const nonEmpty = selected.filter((file) => {
      if (!file.size) { selectionError = `${file.name} is empty.`; return false; }
      if (file.size > MAX_FILE_BYTES) { selectionError = `${file.name} is larger than 100 MB.`; return false; }
      return true;
    });
    const room = BRIEF_LIMITS.files - brief.files.length - pendingFiles.length;
    if (room <= 0) { setFailed(true); setNotice(`A brief can include up to ${BRIEF_LIMITS.files} files.`); return; }
    const accepted = nonEmpty.slice(0, room);
    if (accepted.length < nonEmpty.length) selectionError = `Only the first ${room} files fit; a brief can include ${BRIEF_LIMITS.files}.`;
    if (!campaignId) {
      const known = new Set(pendingFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      onPendingFilesChange([...pendingFiles, ...accepted.filter((file) => !known.has(`${file.name}:${file.size}:${file.lastModified}`))]);
      setFailed(Boolean(selectionError));
      setNotice(selectionError || 'Ready to upload when you save the campaign.');
      return;
    }
    setBusy(true);
    try {
      const uploaded: BrandCampaignFile[] = [];
      for (const file of accepted) uploaded.push((await uploadCampaignFile(campaignId, file)).file);
      setFiles((current) => [...uploaded, ...(current || [])]);
      const refs = uploaded.map((file) => ({file_id: file.file_id, name: file.name}));
      onChange((current) => ({...current, files: [...current.files, ...refs]
        .filter((file, index, all) => all.findIndex((item) => item.file_id === file.file_id) === index)
        .slice(0, BRIEF_LIMITS.files)}));
      setFailed(Boolean(selectionError));
      setNotice(selectionError || `${uploaded.length === 1 ? uploaded[0]!.name : `${uploaded.length} files`} uploaded and added to this brief. Save the campaign to share ${uploaded.length === 1 ? 'it' : 'them'} with creators.`);
    } catch (caught) {
      setFailed(true); setNotice(describe(caught));
    } finally { setBusy(false); }
  }

  async function remove(file: BrandCampaignFile) {
    if (!campaignId || !window.confirm(`Permanently delete ${file.name} from this campaign?`)) return;
    setBusy(true); setNotice(''); setFailed(false);
    try {
      await deleteCampaignFile(campaignId, file.file_id);
      setFiles((current) => (current || []).filter((item) => item.file_id !== file.file_id));
      setNotice(`${file.name} deleted.`);
    } catch (caught) {
      setFailed(true); setNotice(describe(caught));
    } finally { setBusy(false); }
  }

  const attached = new Set(brief.files.map((file) => file.file_id));
  return (
    <section className="brand-file-upload" aria-labelledby="campaign-files-title">
      <div className="brand-file-intro">
        <span className="brand-file-icon"><Icon name="upload" size={22} /></span>
        <div><h3 id="campaign-files-title">Campaign files</h3><p>Add your logo, product photos, brand guidelines, scripts, source footage or other documents. Attached files are shared with creators as part of this brief.</p></div>
      </div>
      <input ref={picker} type="file" multiple hidden onChange={(event) => {
        const selected = [...(event.target.files || [])]; event.target.value = '';
        if (selected.length) void select(selected);
      }} />
      <button className={`brand-file-drop${dragging ? ' dragging' : ''}`} type="button" disabled={busy}
        onClick={() => picker.current?.click()} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()} onDragLeave={() => setDragging(false)} onDrop={(event) => {
          event.preventDefault(); setDragging(false); if (event.dataTransfer.files.length) void select([...event.dataTransfer.files]);
        }}>
        <Icon name={busy ? 'refresh' : 'paperclip'} size={21} />
        <span><b>{busy ? 'Uploading…' : 'Drop files here or choose from your device'}</b><small>Up to 100 MB per file · {BRIEF_LIMITS.files} attachments per brief</small></span>
      </button>
      {pendingFiles.length ? <div className="brand-file-list" aria-label="Files waiting to upload">{pendingFiles.map((file, index) => (
        <div className="brand-file-row" key={`${file.name}:${file.size}:${file.lastModified}`}>
          <span className="brand-file-kind"><Icon name="file" size={16} /></span><span><b>{file.name}</b><small>{humanSize(file.size)} · uploads when campaign is saved</small></span>
          <button type="button" aria-label={`Remove ${file.name}`} onClick={() => onPendingFilesChange(pendingFiles.filter((_, position) => position !== index))}><Icon name="x" size={15} /></button>
        </div>
      ))}</div> : null}
      {files === null ? <p className="brand-file-status">Loading campaign files…</p> : files.length ? <div className="brand-file-list" aria-label="Campaign files">{files.map((file) => {
        const included = attached.has(file.file_id);
        const mustSaveRemoval = savedAttached.current.has(file.file_id) && !included;
        return <div className="brand-file-row" key={file.file_id}>
          <span className="brand-file-kind"><Icon name="file" size={16} /></span><span><b>{file.name}</b><small>{humanSize(file.size)}</small></span>
          <div className="brand-file-actions">
            <a href={briefFileUrl(campaignId!, file.file_id)} download aria-label={`Download ${file.name}`}><Icon name="download" size={15} /></a>
            {included ? <button className="attached" type="button" onClick={() => detach(file.file_id)}><Icon name="check" size={14} />Included</button>
              : <button type="button" disabled={brief.files.length >= BRIEF_LIMITS.files} onClick={() => attach(file)}><Icon name="paperclip" size={14} />Add</button>}
            {!included ? <button className="delete" type="button" disabled={mustSaveRemoval || busy} title={mustSaveRemoval ? 'Save the campaign before deleting this file' : 'Delete file'} aria-label={`Delete ${file.name}`} onClick={() => void remove(file)}><Icon name="trash" size={15} /></button> : null}
          </div>
        </div>;
      })}</div> : campaignId ? <p className="brand-file-status">No files uploaded yet.</p> : null}
      {notice ? <p className={`brand-file-status${failed ? ' error' : ''}`} role={failed ? 'alert' : 'status'}>{notice}</p> : null}
    </section>
  );
}

/** The brief's sections as form fields, inside the campaign editor. */
export function BriefFields({brief, onChange, error, campaignId, pendingFiles, onPendingFilesChange}: {
  brief: BriefSections;
  onChange: Dispatch<SetStateAction<BriefSections>>;
  error?: string;
  campaignId?: string;
  pendingFiles: File[];
  onPendingFilesChange(files: File[]): void;
}) {
  const [custom, setCustom] = useState('');
  function set<K extends keyof BriefSections>(key: K, value: BriefSections[K]) {
    onChange({...brief, [key]: value});
  }
  const extra = brief.format.filter((item) => !FORMAT_PRESETS.includes(item));
  function toggle(value: string) {
    set('format', brief.format.includes(value) ? brief.format.filter((item) => item !== value) : [...brief.format, value].slice(0, BRIEF_LIMITS.format));
  }
  function addCustom() {
    const value = custom.replace(/\s+/g, ' ').trim().slice(0, 40);
    if (value && !brief.format.includes(value)) set('format', [...brief.format, value].slice(0, BRIEF_LIMITS.format));
    setCustom('');
  }
  const examples = brief.examples.length ? brief.examples : [{url: ''}];
  return (
    <div className="brief-fields">
      <label className={`brand-field${error ? ' field-error' : ''}`}>
        <span>The idea</span>
        <textarea rows={3} maxLength={BRIEF_LIMITS.idea} value={brief.idea} placeholder="Show your real morning. Our drink is how you wake up, not an ad." onChange={(event) => set('idea', event.target.value)} />
        <small>One or two sentences, in the creator's words. Creators read this first.</small>
        {error ? <em>{error}</em> : null}
      </label>
      <Lines label="Must include" hint="Numbered. The creator ticks each one before sending a video." items={brief.must} max={BRIEF_LIMITS.must} ordered placeholder="Product on screen in the first 2 seconds" onChange={(items) => set('must', items)} />
      <Lines label="Avoid" hint="What gets a video sent back." items={brief.avoid} max={BRIEF_LIMITS.avoid} placeholder="Other brands in the shot" onChange={(items) => set('avoid', items)} />
      <fieldset className="brand-field brief-block">
        <legend><span>Format</span><small>Tap what applies; add your own.</small></legend>
        <div className="chip-row">
          {[...FORMAT_PRESETS, ...extra].map((value) => (
            <button key={value} type="button" className={`choice-chip${brief.format.includes(value) ? ' active' : ''}`} aria-pressed={brief.format.includes(value)} onClick={() => toggle(value)}>{value}</button>
          ))}
        </div>
        <div className="brief-custom">
          <input value={custom} maxLength={40} placeholder="Another label, e.g. Filmed outdoors" aria-label="Add a format label"
            onChange={(event) => setCustom(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addCustom(); } }} />
          <button className="btn btn-light btn-sm" type="button" disabled={!custom.trim() || brief.format.length >= BRIEF_LIMITS.format} onClick={addCustom}>Add</button>
        </div>
      </fieldset>
      <fieldset className="brand-field brief-block">
        <legend><span>Example videos</span><small>Links that show the direction, https only.</small></legend>
        <div className="brief-lines">
          {examples.map((example, index) => {
            const bad = badExampleUrl(example.url);
            return (
              <div key={index}>
                <div className="brief-line example">
                  <i aria-hidden="true"><Icon name="video" size={13} /></i>
                  <input value={example.url} inputMode="url" placeholder="https://www.tiktok.com/@…/video/…" aria-label={`Example ${index + 1} link`} aria-invalid={bad || undefined}
                    onChange={(event) => set('examples', examples.map((item, position) => (position === index ? {...item, url: event.target.value} : item)))} />
                  {examples.length > 1 || example.url ? <button type="button" aria-label={`Remove example ${index + 1}`} onClick={() => set('examples', examples.filter((_, position) => position !== index))}><Icon name="x" size={14} /></button> : null}
                </div>
                {bad ? <em className="brief-error">Use a full https:// link.</em> : null}
              </div>
            );
          })}
        </div>
        <button className="text-button brief-add" type="button" disabled={examples.length >= BRIEF_LIMITS.examples} onClick={() => set('examples', [...examples, {url: ''}])}><Icon name="plus" size={14} />Add an example</button>
      </fieldset>
      <label className="brand-field">
        <span>Caption</span>
        <textarea rows={3} maxLength={BRIEF_LIMITS.caption} value={brief.caption} placeholder="My mornings start with @yourbrand now. #yourbrand" onChange={(event) => set('caption', event.target.value)} />
        <small>What goes under the post. Creators copy it when they post, and #ad is added when it is missing. Leave it empty to let them write their own.</small>
      </label>
      <CampaignFilesField brief={brief} campaignId={campaignId} pendingFiles={pendingFiles} onChange={onChange} onPendingFilesChange={onPendingFilesChange} />
    </div>
  );
}

/**
 * A brief as a short read: on the campaign page, and beside a video under
 * publication. With the campaign, its files are links the brand can download.
 */
export function BriefRead({brief, compact = false, campaignId}: {brief: BriefSections; compact?: boolean; campaignId?: string}) {
  const clean = cleanBrief(brief);
  if (!clean.idea && !clean.must.length && !clean.avoid.length && !clean.format.length && !clean.examples.length && !clean.files.length && !clean.caption) {
    return <p className="muted">No brief yet.</p>;
  }
  return (
    <div className={`brief-read${compact ? ' compact' : ''}`}>
      {clean.idea ? <div><span className="brief-label">The idea</span><p>{clean.idea}</p></div> : null}
      {clean.must.length ? (
        <div><span className="brief-label">Must include</span>
          <ol>{clean.must.map((item, index) => <li key={index}><i>{index + 1}</i>{item}</li>)}</ol>
        </div>
      ) : null}
      {clean.avoid.length ? (
        <div><span className="brief-label">Avoid</span>
          <ul>{clean.avoid.map((item, index) => <li key={index}><i><Icon name="x" size={11} /></i>{item}</li>)}</ul>
        </div>
      ) : null}
      {clean.format.length ? <div className="chip-row">{clean.format.map((item) => <span className="choice-chip active" key={item}>{item}</span>)}</div> : null}
      {clean.examples.length ? (
        <div className="brief-links">{clean.examples.map((example, index) => <a key={index} href={example.url} target="_blank" rel="noopener"><Icon name="video" size={14} />{exampleHost(example.url)}<Icon name="external" size={13} /></a>)}</div>
      ) : null}
      {clean.files.length && campaignId ? (
        <div><span className="brief-label">Files for creators</span>
          <div className="brief-files">{clean.files.map((file) => <a key={file.file_id} href={briefFileUrl(campaignId, file.file_id)} download><Icon name="download" size={14} />{file.name || 'File'}</a>)}</div>
        </div>
      ) : clean.files.length ? <p className="muted">{clean.files.length} {clean.files.length === 1 ? 'file' : 'files'} for creators: {clean.files.map((file) => file.name).join(', ')}</p> : null}
      {clean.caption ? <div><span className="brief-label">Caption</span><p className="brief-caption">{clean.caption}</p></div> : null}
    </div>
  );
}
